// React and hooks
import { useCallback, useState, useEffect } from "react";

// Third-party libraries
import { useTranslation } from "react-i18next";

// Components

import ManualBalanceInfoModal from "../manualBalanceInfoModal";
import WithdrawalSuccessModal from "../withdrawalSuccessModal";

// Styles
import styles from "./styles.module.scss";
import { useWallet } from "../../../../providers/wallet/wallet.provider";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../setup/constants";
import TooltipBubble from "../../../../components/common/TooltipBubble";
import { formatAmount } from "../../../../setup/util/wallet";
import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../../../../components/common/PrimaryButton";
import { AuthConfig } from "../../../../setup/context/config/authConfig";

/**
 * ManualBalanceCard Component
 *
 * Displays a card that encourages users to withdraw their available balance
 * in bulk instead of individual transaction withdrawals.
 *
 * Features:
 * - Motivational message to encourage bulk withdrawals
 * - Benefits explanation
 * - Withdraw button with custom styling
 * - Visual icon representation
 * - Mobile-friendly info modal on exclamation icon click
 * - Dropdown menu for additional actions (manage payment method, disable manual withdrawal)
 */

const ManualBalanceCard = () => {
  // Translation hook for internationalization
  const { t } = useTranslation();
  const navigate = useNavigate();

  const myInfo = AuthConfig.getMyInfo();
  const { userIdentification, id: userId, profile } = AuthConfig.getUserInfo();

  // State for managing the info modal visibility
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const availableBalanceAmount = useWallet(
    (state) => state.availableBalanceAmount
  );

  const withdrawalLoading = useWallet((state) => state.withdrawalLoading);

  const createWithdrawal = useWallet(
    (state) => state.createOrganizationWithdrawal
  );

  // Handle clicking outside modal to close it
  useEffect(() => {
    const handleClickOutside = () => {
      if (infoModalOpen) {
        setInfoModalOpen(false);
      }
    };

    if (infoModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [infoModalOpen]);

  // Auto-open bank account modal if bankStatus present in URL
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const bankStatus = searchParams.get("bankStatus");
      if (bankStatus) {
        // Remove bankStatus from URL so it doesn't retrigger
        searchParams.delete("bankStatus");
        const newSearch = searchParams.toString();
        navigate(
          { search: newSearch ? `?${newSearch}` : "" },
          { replace: true }
        );
      }
    } catch (e) {
      // noop
    }
  }, [navigate]);

  /**
   * Handles the bulk withdrawal approval action
   * Creates a withdrawal request for the available balance
   */
  const handleBulkWithdrawal = useCallback(async (): Promise<void> => {
    if (withdrawalLoading) return; // Prevent multiple calls

    try {
      const response = await createWithdrawal(
        userIdentification || userId,
        availableBalanceAmount.toString(),
        profile?.fullName || myInfo?.profile?.fullName || ""
      );
      if (!response.success) return;

      // If we reach here, withdrawal was successful

      setShowSuccessModal(true);

      setInfoModalOpen(false);
    } catch (error) {
      console.log("error here", error);
      console.error("Withdrawal failed:", error);
    }
  }, [
    withdrawalLoading,
    createWithdrawal,
    userIdentification,
    userId,
    availableBalanceAmount,
    profile?.fullName,
    myInfo?.profile?.fullName,
  ]);

  /**
   * Handles opening the info modal on mobile
   * Shows the modal when exclamation icon is clicked on mobile devices
   */
  const handleInfoClick = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width < 992 || (width >= 1000 && height >= 1900)) {
      setInfoModalOpen(true); // mirror mobile behavior at >=1000px
    }
  }, []);

  /**
   * Handles closing the info modal
   */
  const handleCloseInfoModal = useCallback(() => {
    setInfoModalOpen(false);
  }, []);

  /**
   * Handles the manage payment method action
   */
  const handleManagePaymentMethod = useCallback(() => {
    setInfoModalOpen(false);
    handleBulkWithdrawal();
  }, [handleBulkWithdrawal]);

  const navigateToBankPage = useCallback(() => {
    navigate(`${ROUTES.SETTINGS}`);
  }, [navigate]);

  return (
    <>
      <div className={styles.manual_balance_container}>
        <div className={styles.header}>
          <div className={styles.title_container}>
            <h4>{t("balance_bulk_avaialble_balance_title")}</h4>
            <div className={styles.mob_none}>
              <TooltipBubble
                content={t("balance_bulk_avaialble_balance_info")}
                position="bottom"
                trigger="hover">
                <img
                  src="/assets/images/svg/exclamation_icon.svg"
                  alt="exclamation"
                  className={styles.exclamation_icon}
                />
              </TooltipBubble>
            </div>
            <div className={styles.mob_visible}>
              <img
                src="/assets/images/svg/exclamation_icon.svg"
                alt="exclamation"
                className={styles.exclamation_icon}
                onClick={handleInfoClick}
                style={{ cursor: "pointer" }}
                title="Click for info (mobile)"
              />
            </div>
          </div>
        </div>

        <h2
          className={styles.withdraw_amount}
          style={{ color: availableBalanceAmount <= 0 ? "red" : "" }}>
          {formatAmount(availableBalanceAmount)} {t("lbl_saudi_currency")}
        </h2>

        <div className={styles.button_container}>
          <div className={styles.button}>
            <PrimaryButton
              id="contract_details_add_adress_confirm"
              buttonType={
                availableBalanceAmount <= 0
                  ? PrimaryButtonType.DISABLED_DARK
                  : PrimaryButtonType.PRIMARY_DARK
              }
              size={PrimaryButtonSize.MEDIUM}
              onClick={handleManagePaymentMethod}
              disabled={availableBalanceAmount <= 0}>
              <p className={classNames(styles.button_text, styles.whiteText)}>
                {t("balance_bulk_request_withdraw_title")}
              </p>
            </PrimaryButton>
          </div>
        </div>

        <p
          className={classNames(styles["bank_link"], styles["mob_none"])}
          onClick={() => navigateToBankPage()}
          style={{ cursor: "pointer" }}>
          {t("balance_bulk_manage_bank_account_button")}
        </p>
      </div>

      {/* Modal for Mobile */}

      <ManualBalanceInfoModal
        open={infoModalOpen}
        onClose={handleCloseInfoModal}
      />

      {/* Success modal lifted to parent; keep local state for backwards compatibility */}
      {/* It can still render locally if not lifted, but parent will also show it */}
      <WithdrawalSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default ManualBalanceCard;
